import React, { useState } from 'react';
import { CheckCircle2, Plus, Trash2, Image, Film, Monitor } from 'lucide-react';

const RENDERERS = [
  { name: 'veo',        label: 'Veo',        version: '3.0',  vendor: 'Google DeepMind' },
  { name: 'kling',      label: 'Kling',      version: '2.0',  vendor: 'Kuaishou'        },
  { name: 'runway',     label: 'Runway',     version: 'gen4', vendor: 'Runway ML'       },
  { name: 'seedance',   label: 'Seedance',   version: '1.0',  vendor: 'ByteDance'       },
  { name: 'higgsfield', label: 'Higgsfield', version: '1.0',  vendor: 'Higgsfield AI'  },
  { name: 'hailuo',     label: 'Hailuo',     version: '2.0',  vendor: 'MiniMax'         }
];

const ReferenceList = ({ refs, onAdd, onUpdate, onRemove, urlPlaceholder, icon: Icon, emptyLabel }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-5">
    <div className="flex items-center justify-between mb-3">
      <h3 className="font-medium text-gray-800 flex items-center gap-2">
        <Icon className="w-4 h-4 text-gray-500" />
        {emptyLabel.replace('Nessun', '').replace(' aggiunto', '').replace(' aggiunta', '').trim()}
      </h3>
      <button
        onClick={onAdd}
        className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium"
      >
        <Plus className="w-4 h-4" />
        Aggiungi
      </button>
    </div>
    {refs.length === 0 ? (
      <p className="text-sm text-gray-400 italic">{emptyLabel}</p>
    ) : (
      <div className="space-y-3">
        {refs.map((ref, i) => (
          <div key={i} className="flex gap-2 items-start">
            <div className="flex-1 space-y-2">
              <input
                type="url"
                placeholder={urlPlaceholder}
                value={ref.url}
                onChange={(e) => onUpdate(i, 'url', e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
              <input
                type="text"
                placeholder="Descrizione (es. avatar reference, stile lighting...)"
                value={ref.description}
                onChange={(e) => onUpdate(i, 'description', e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>
            <button
              onClick={() => onRemove(i)}
              className="text-red-400 hover:text-red-500 mt-1.5 flex-shrink-0"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    )}
  </div>
);

const Contract162View = ({ execution, onApprove, loading }) => {
  const [selectedRenderer, setSelectedRenderer] = useState('veo');
  const [rendererVersion, setRendererVersion] = useState('3.0');
  const [imageRefs, setImageRefs] = useState([]);
  const [videoRefs, setVideoRefs] = useState([]);

  const handleRendererSelect = (name) => {
    setSelectedRenderer(name);
    const r = RENDERERS.find(r => r.name === name);
    setRendererVersion(r.version);
  };

  const addImageRef  = () => setImageRefs(prev => [...prev, { url: '', description: '' }]);
  const removeImageRef = (i) => setImageRefs(prev => prev.filter((_, idx) => idx !== i));
  const updateImageRef = (i, field, value) => {
    setImageRefs(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: value } : r));
  };

  const addVideoRef  = () => setVideoRefs(prev => [...prev, { url: '', description: '' }]);
  const removeVideoRef = (i) => setVideoRefs(prev => prev.filter((_, idx) => idx !== i));
  const updateVideoRef = (i, field, value) => {
    setVideoRefs(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: value } : r));
  };

  const handleConfirm = () => {
    const curatedOutput = {
      renderer_configuration: {
        target_renderer: {
          name: selectedRenderer,
          version: rendererVersion
        },
        image_references: imageRefs.filter(r => r.url.trim()),
        video_references: videoRefs.filter(r => r.url.trim())
      }
    };
    onApprove(curatedOutput);
  };

  if (execution?.status === 'approved') {
    const cfg = execution.outputData?.renderer_configuration;
    return (
      <div className="h-full overflow-y-auto">
        <div className="p-6">
          <div className="bg-green-50 border border-green-200 rounded-lg p-5">
            <div className="flex items-center gap-2 text-green-700 mb-3">
              <CheckCircle2 className="w-5 h-5" />
              <span className="font-semibold">Configurazione confermata</span>
            </div>
            {cfg && (
              <div className="space-y-1 text-sm text-green-800">
                <p>
                  <span className="font-medium">Renderer: </span>
                  {cfg.target_renderer?.name?.toUpperCase()} v{cfg.target_renderer?.version}
                </p>
                <p>
                  <span className="font-medium">Image references: </span>
                  {cfg.image_references?.length || 0}
                </p>
                <p>
                  <span className="font-medium">Video references: </span>
                  {cfg.video_references?.length || 0}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
    <div className="space-y-5 p-6 max-w-2xl mx-auto">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Configurazione Renderer</h2>
        <p className="text-sm text-gray-500 mt-1">
          Scegli il modello di generazione video e aggiungi eventuali reference da passare al renderer.
        </p>
      </div>

      {/* Renderer selection */}
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <h3 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
          <Monitor className="w-4 h-4 text-gray-500" />
          Modello di generazione video
        </h3>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {RENDERERS.map((r) => (
            <button
              key={r.name}
              onClick={() => handleRendererSelect(r.name)}
              className={`p-3 rounded-lg border-2 text-left transition-all ${
                selectedRenderer === r.name
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
              }`}
            >
              <div className="font-semibold text-gray-900 text-sm">{r.label}</div>
              <div className="text-xs text-gray-500 mt-0.5">{r.vendor}</div>
              <div className="text-xs text-gray-400 mt-1">v{r.version}</div>
            </button>
          ))}
        </div>

        <div className="mt-4 flex items-center gap-3">
          <label className="text-sm text-gray-600 whitespace-nowrap">Versione:</label>
          <input
            type="text"
            value={rendererVersion}
            onChange={(e) => setRendererVersion(e.target.value)}
            className="border border-gray-300 rounded px-3 py-1.5 text-sm w-28 focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
        </div>
      </div>

      {/* Image references */}
      <ReferenceList
        refs={imageRefs}
        onAdd={addImageRef}
        onUpdate={updateImageRef}
        onRemove={removeImageRef}
        urlPlaceholder="https://... (URL immagine)"
        icon={Image}
        emptyLabel="Nessuna image reference aggiunta"
      />

      {/* Video references */}
      <ReferenceList
        refs={videoRefs}
        onAdd={addVideoRef}
        onUpdate={updateVideoRef}
        onRemove={removeVideoRef}
        urlPlaceholder="https://... (URL video)"
        icon={Film}
        emptyLabel="Nessun video reference aggiunto"
      />

      {/* Confirm */}
      <div className="flex justify-end pt-2">
        <button
          onClick={handleConfirm}
          disabled={loading}
          className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-6 py-2.5 rounded-lg font-medium flex items-center gap-2 transition-colors"
        >
          <CheckCircle2 className="w-4 h-4" />
          {loading ? 'Salvataggio...' : 'Conferma configurazione'}
        </button>
      </div>
    </div>
    </div>
  );
};

export default Contract162View;
